import { type ReactNode, type ButtonHTMLAttributes } from 'react';
import { Link, type LinkProps } from 'react-router-dom';

type ButtonVariant = 'primary' | 'secondary' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface BaseButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
  className?: string;
}

type ButtonAsButton = BaseButtonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseButtonProps> & {
    as?: 'button';
  };

type ButtonAsLink = BaseButtonProps &
  Omit<LinkProps, keyof BaseButtonProps> & {
    as: 'link';
  };

type ButtonAsAnchor = BaseButtonProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseButtonProps> & {
    as: 'anchor';
    href: string;
  };

type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsAnchor;

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-accent-500 text-roughneck-950 hover:bg-accent-400 focus-visible:outline-accent-500',
  secondary:
    'border border-hairline bg-transparent text-roughneck-100 hover:border-roughneck-600 hover:bg-roughneck-800/60 focus-visible:outline-accent-500',
  ghost:
    'text-roughneck-300 hover:text-white hover:bg-roughneck-800/60 focus-visible:outline-accent-500',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-5 py-2.5 text-sm',
  lg: 'px-6 py-3 text-base',
};

const baseClasses =
  'inline-flex items-center justify-center rounded font-semibold transition-colors duration-200 ease-out focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

export function Button(props: ButtonProps) {
  const {
    variant = 'primary',
    size = 'md',
    children,
    className = '',
    ...rest
  } = props;

  const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;

  if (props.as === 'link') {
    const { as: _as, ...linkProps } = rest as Omit<ButtonAsLink, keyof BaseButtonProps>;
    return (
      <Link className={classes} {...linkProps}>
        {children}
      </Link>
    );
  }

  if (props.as === 'anchor') {
    const { as: _as, ...anchorProps } = rest as Omit<ButtonAsAnchor, keyof BaseButtonProps>;
    return (
      <a className={classes} {...anchorProps}>
        {children}
      </a>
    );
  }

  const { as: _as, ...buttonProps } = rest as Omit<ButtonAsButton, keyof BaseButtonProps>;
  return (
    <button className={classes} {...buttonProps}>
      {children}
    </button>
  );
}
